import React, { forwardRef } from 'react';
import { ExclamationCircleIcon, CheckCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface FormInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  success?: string;
  helperText?: string;
  required?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
  showPasswordToggle?: boolean;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(({
  label,
  error,
  success,
  helperText,
  required,
  loading,
  leftIcon,
  rightIcon,
  size = 'md',
  variant = 'default',
  showPasswordToggle,
  type = 'text',
  className = '',
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [focused, setFocused] = React.useState(false);

  const inputType = type === 'password' && showPassword ? 'text' : type;

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-3 py-2.5 text-sm',
    lg: 'px-4 py-3 text-base',
  };

  const variantClasses = {
    default: 'border border-gray-300 bg-white',
    filled: 'border-0 bg-gray-100',
    outlined: 'border-2 border-gray-300 bg-transparent',
  };

  const getStateClasses = () => {
    if (error) {
      return 'border-red-500 ring-red-500 focus:border-red-500 focus:ring-red-500';
    }
    if (success) {
      return 'border-green-500 ring-green-500 focus:border-green-500 focus:ring-green-500';
    }
    if (focused) {
      return 'border-[#008B8B] ring-[#008B8B] focus:border-[#008B8B] focus:ring-[#008B8B]';
    }
    return 'focus:border-[#008B8B] focus:ring-[#008B8B]';
  };

  const inputClasses = [
    'w-full rounded-lg transition-colors duration-200',
    'focus:outline-none focus:ring-2 focus:ring-opacity-50',
    'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
    sizeClasses[size],
    variantClasses[variant],
    getStateClasses(),
    leftIcon ? 'pl-10' : '',
    (rightIcon || showPasswordToggle || error || success || loading) ? 'pr-10' : '',
    className,
  ].filter(Boolean).join(' ');

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    props.onBlur?.(e);
  };

  return (
    <div className="w-full">
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className="relative">
        {/* Left Icon */}
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className="text-gray-400">{leftIcon}</div>
          </div>
        )}

        {/* Input */}
        <input
          ref={ref}
          type={inputType}
          className={inputClasses}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...props}
        />

        {/* Right Icons */}
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center space-x-1">
          {/* Loading Spinner */}
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-[#008B8B]"></div>
          )}

          {/* Success Icon */}
          {success && !loading && (
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          )}

          {/* Error Icon */}
          {error && !loading && (
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          )}

          {/* Password Toggle */}
          {showPasswordToggle && type === 'password' && !loading && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          )}

          {/* Custom Right Icon */}
          {rightIcon && !loading && !error && !success && (
            <div className="text-gray-400">{rightIcon}</div>
          )}
        </div>
      </div>

      {/* Helper Text / Error / Success Message */}
      {(error || success || helperText) && (
        <div className="mt-1 text-sm">
          {error && (
            <div className="text-red-600 flex items-center">
              <ExclamationCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              {typeof error === 'string' ? error : (error as any)?.message || 'Invalid input'}
            </div>
          )}
          {success && !error && (
            <div className="text-green-600 flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              {success}
            </div>
          )}
          {helperText && !error && !success && (
            <div className="text-gray-500">{helperText}</div>
          )}
        </div>
      )}
    </div>
  );
});

FormInput.displayName = 'FormInput';

export default FormInput;